import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, CheckCircle2, ExternalLink, Phone, ShieldCheck, ArrowRight } from 'lucide-react';

/**
 * PayFaida — client-facing payment landing page.
 *
 * Query params:
 *   ctrl  — Faida Fund Control Number (12-digit GePG)
 *   amt   — Desired payment amount in TZS
 *
 * The current MVP shows the user crystal-clear instructions for each mobile
 * wallet (M-Pesa / Mixx by Yas / Airtel Money) plus the WHI web portal. A
 * direct Selcom-GePG payment button is Phase 2 (requires Selcom merchant
 * permission for GePG routing to a third-party Control Number).
 */
export default function PayFaida() {
  const [params] = useSearchParams();
  const ctrl = params.get('ctrl') || '';
  const amtRaw = params.get('amt') || '10000';
  const amount = useMemo(() => {
    const n = parseInt(String(amtRaw).replace(/\D/g, ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 10000;
  }, [amtRaw]);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    document.title = 'Pay Faida Fund — Wekeza-Smart';
  }, []);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(''), 1800);
    } catch (_) { /* no-op */ }
  };

  const fmt = (n) => Number(n).toLocaleString('en-US');

  const wallets = [
    { name: 'M-Pesa (Vodacom)', color: 'bg-red-600', steps: [
      'Fungua Menu ya M-Pesa',
      'Chagua Lipa Bili',
      'Chagua Malipo ya Serikali',
      `Weka Control Number: ${ctrl}`,
      `Weka kiasi: TZS ${fmt(amount)}`,
      'Thibitisha kwa PIN yako',
    ]},
    { name: 'Mixx by Yas (Tigo Pesa)', color: 'bg-blue-600', steps: [
      'Fungua Menu ya Mixx by Yas',
      'Chagua Lipa Bili',
      'Chagua Malipo ya Serikali (GePG)',
      `Weka Control Number: ${ctrl}`,
      `Weka kiasi: TZS ${fmt(amount)}`,
      'Thibitisha kwa PIN yako',
    ]},
    { name: 'Airtel Money', color: 'bg-rose-600', steps: [
      'Piga *150*60#',
      'Chagua Lipa Bili',
      'Chagua Malipo ya Serikali',
      `Weka Control Number: ${ctrl}`,
      `Weka kiasi: TZS ${fmt(amount)}`,
      'Thibitisha kwa PIN yako',
    ]},
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-emerald-900 to-slate-950 text-white px-4 py-8">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-5 h-5 text-emerald-300" />
          <span className="text-xs uppercase tracking-wider text-emerald-200 font-semibold">Wekeza-Smart × Faida Fund</span>
        </div>

        <h1 className="text-3xl font-black leading-tight mb-2" data-testid="pay-faida-title">
          Lipa Faida Fund kwa Hatua Rahisi
        </h1>
        <p className="text-white/60 text-sm mb-8">
          Faida Fund ni mfuko wa WHI ulioidhinishwa na CMSA. Lipa kiasi ulichotaka kupitia M-Pesa, Mixx by Yas, au Airtel Money.
        </p>

        {/* Highlight card: Control Number + Amount */}
        <div className="bg-gradient-to-br from-amber-500/20 via-emerald-800/40 to-emerald-950/60 border border-amber-300/30 rounded-2xl p-5 mb-6" data-testid="pay-faida-control-card">
          <p className="text-xs text-amber-200 font-semibold uppercase tracking-wider mb-2">Control Number (GePG)</p>
          <div className="flex items-center justify-between gap-2 mb-4">
            <code className="text-2xl font-mono font-black tracking-wider text-amber-100" data-testid="pay-faida-ctrl">
              {ctrl || '—'}
            </code>
            {ctrl && (
              <button
                onClick={() => copy(ctrl, 'ctrl')}
                className="shrink-0 bg-amber-400 hover:bg-amber-300 text-emerald-950 text-xs font-bold px-3 py-2 rounded-lg transition-all"
                data-testid="pay-faida-copy-ctrl"
              >
                {copied === 'ctrl' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            )}
          </div>
          <p className="text-xs text-amber-200 font-semibold uppercase tracking-wider mb-1">Kiasi cha Kulipa</p>
          <p className="text-xl font-bold" data-testid="pay-faida-amount">TZS {fmt(amount)}</p>
        </div>

        {/* Mobile wallet instructions */}
        <div className="space-y-4 mb-8" data-testid="pay-faida-wallets">
          {wallets.map((w, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`pay-faida-wallet-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${w.color}`}>
                  <Phone className="w-4 h-4" />
                </span>
                <h3 className="text-base font-bold">{w.name}</h3>
              </div>
              <ol className="list-decimal list-inside text-sm text-white/80 space-y-1 pl-1">
                {w.steps.map((step, j) => (
                  <li key={j} className="leading-relaxed">{step}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>

        {/* Fallback: web portal */}
        <a
          href={`https://fas.whi.go.tz/authentication/login`}
          target="_blank" rel="noopener noreferrer"
          className="block bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl p-4 flex items-center justify-between gap-3 mb-6 transition"
          data-testid="pay-faida-web-portal"
        >
          <div>
            <p className="text-sm font-semibold">Au lipa kwa kadi (VISA/Mastercard)</p>
            <p className="text-xs text-white/60 mt-0.5">Ingia kwenye lango la WHI → BUY UNITS</p>
          </div>
          <ExternalLink className="w-4 h-4 text-white/50 shrink-0" />
        </a>

        {/* Help footer */}
        <div className="text-center text-xs text-white/40 space-y-1">
          <p>Baada ya kulipa, utapokea SMS kutoka WHI inayothibitisha ununuzi.</p>
          <p>Msaada: +255 22 292 2211 · wekeza@whi.go.tz</p>
        </div>

        {/* Return CTA */}
        <a
          href="/faida"
          className="mt-8 block text-center bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-black py-3 rounded-xl flex items-center justify-center gap-2"
          data-testid="pay-faida-back-to-ai"
        >
          Fuatilia malipo yako <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
